import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import MS from 'services/data/MetricService';
import Helmet from 'react-helmet';
import { makeWaitForData } from 'components/shared/WaitForData';
import DateArrays from 'lib/utils/DateArrays';
import { useHistory } from 'react-router';
import { isMobile, throttle } from 'lib/utils/UiUtils';
import MerDesktop from './components/MerDesktop';
import MerMobile from './components/MerMobile';
import fetchPredictionResult from './utils/fetchPredictionResult';
import Config from 'lib/Config';
import { orZero, average } from 'utils/NumUtils';
import { CampaignDataFilter } from 'models/CampaignObjects';
import { PageHelper } from 'helpers/PageHelper';
import { getAccountProvider, getAccountProviders, getAccountTimezone, getGoal, getGoals, getInnerError, getProvider, renderError, renderLoader, renderNoDataMessage } from 'utils/adaya/AdayaUtils';
import Loader from 'components/shared/app/Loader';
import { ProviderNames } from 'lib/data/models/Providers';
import { Goal } from 'lib/data/models/Goals';
import GoalHelper from 'lib/helpers/GoalHelper';
import { defaultMetricItems, getInitialTabStates, predictionMetricItems, tabOptions, TabState } from './utils/utils';
import { getLogger } from 'lib/utils/logging';
import { createIsoTimeRange } from 'lib/utils/DateUtils';
import { ManagedRequest, RequestStatus } from 'lib/RequestManager';
import { ErrorMessages } from 'lib/AppErrors';
import NotificationService from 'lib/services/NotificationService';
import { processTabData } from './utils/processTabData';
import MerGraphTest from './components/MerGraphTest';
import './style.scss';

const { log, warn } = getLogger('Mer', { enabled: false });

const pageHelper = new PageHelper();

const Mer = ({ auth, ...props }) => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);

    const [activeTab, setActiveTab] = useState('last7days');
    const [tabStates, setTabStates] = useState(getInitialTabStates());
    const [metricItems, setMetricItems] = useState(defaultMetricItems);
    const [selectedMetric, setSelectedMetric] = useState('mer');
    const [predictionState, setPredictionState] = useState({
        data: null,
        error: null,
        progress: null
    });

    const user = auth?.user;
    const account = user?.activeAccount;
    const timezone = getAccountTimezone(account);

    // todo: get goal from GoalHelper
    const merGoal = getGoal(account, Goal.Mer);

    // old: ////////////////////////
    const [mobile, setMobile] = useState(isMobile());

    useEffect(async () => {
        await fetchAllData();
    }, [account]);

    // data options change that requires new data
    useEffect(async () => {
        updateMetricOptions();
    }, [activeTab]);

    // new tab data is loaded
    useEffect(async () => {
        updateViewData();
    }, [tabStates]);

    // view option change that doesn't require new data set
    useEffect(async () => {
        updateViewData();
    }, [selectedMetric]);

    // When prediction data is ready, set the mer averages for the tabs, and trigger data refresh if necessary...
    useEffect(() => {
        log(`Prediction state changed`, predictionState);
        if (tabStates && predictionState.data && predictionState.data.length > 0) {
            log(`Prediction states loaded...`, predictionState.data)
            updateTabState('last7days', { merPrediction: Math.abs(average(predictionState.data.slice(0, 7))).toFixed(2) });
            updateTabState('last30days', { merPrediction: Math.abs(average(predictionState.data.slice(0, 30))).toFixed(2) });

            // When prediction data is ready, if on the prediction view, manually trigger re-render to re-format the graph data
            if (selectedMetric === 'prediction') {
                updateViewData();
            }
        }
    }, [predictionState]);

    const fetchAllData = async () => {
        try {
            log(`fetchAllData`, account);
            if (!account) return;

            await Promise.allSettled([
                fetchDataSets(),
                //fetchMerPredictions()
            ]);
        } catch (e) {
            console.error("fetchAllData error", e);
            setError(getInnerError(e));
        }
    };

    // fetches the page data sets in parallel, and they will load individually
    const fetchDataSets = async () => {
        try {
            let dataSets = Object.keys(tabOptions);
            log(`fetchDataSets`, dataSets);
            await Promise.all(
                dataSets.map(async ds => await fetchData(ds))
            );
        } catch (e) {
            throw e;
        }
    };

    const fetchData = async (tab) => {
        try {
            let filter = getFilterFromSelection(tab);
            let r = await pageHelper.getDailyCampaignDataRequest(filter);
            onDataResponse(r, tab);
        } catch (e) {
            // error in specific tab, try to update state:
            const error = getInnerError(e);
            log(`Exception fetching tab data: `, error);
            NotificationService.notifyError('API Error', error);
            updateTabState(tab, { state: TabState.Error, error });
        }
    };

    // update a single tab's state, ie. when it's data is returned.
    const updateTabState = (tab, state) => {
        let ts = tabStates[tab];
        Object.assign(ts, state);
        setTabStates({ ...tabStates, tab: ts });
        return ts;
    };

    const fetchMerPredictions = async () => {
        try {
            log(`fetchMerPredictions..`);
            let accountId = account._id;
            let pr = await fetchPredictionResult(accountId, (progress) => setPredictionState({ ...predictionState, progress }));
            log(`See prediction result?`, pr);
            if (pr?.dailyMER) {
                setPredictionState({ ...predictionState, data: pr.dailyMER });
            } else {
                console.warn(`Could not load mer prediction, got:`, pr);
                let error = getInnerError(pr);
                setPredictionState({ ...predictionState, error });
            }
        } catch (e) {
            log(`fetchPredictionResult error:`, error);
            let error = getInnerError(e);
            NotificationService.notifyError('Error Retrieving MER', error);
            setPredictionState({ ...predictionState, error });
        }
    };

    // Handles the response in any way it needs. This should not return anything.
    const onDataResponse = (r: ManagedRequest, tab: string): void => {
        try {
            if (!r) throw "Response was empty.";
            //log('onDataResponse', tab, r);

            if (r.status == RequestStatus.FINISHED) {
                // process data/calculate metrics, and update this tab's state:
                let formattedData = processTabData(tab, r.response, selectedMetric, predictionState);
                //log(`Tab data processed.`, tab, formattedData);
                return updateTabState(tab, { state: TabState.Loaded, data: formattedData });
            } else if (r.status == RequestStatus.ERROR) {
                return updateTabState(tab, { state: TabState.Error, error: getInnerError(r) });
            } else {
                // shouldn't get here, it's handled through exceptions mostly, but just in case.
                console.warn("Unknown request response:", r, `for tab: ${tab}`);
                return updateTabState(tab, { state: TabState.Error, error: "Unknown state: " + getInnerError(r) });
            }
        } catch (e) {
            log(`Exception`, e);
            throw e;
        }
    };

    // MER needs all campaign data except Adaya
    const getFilterFromSelection = (tab): CampaignDataFilter => {
        const timeRange = createIsoTimeRange(tab);
        if (!timeRange) throw `Time range could not be created from given value: ${tab}`;
        if (!account) throw `No account found for the current user.`;
        return {
            account,
            timeRange,
            providers: getAccountProviders(account, [ProviderNames.ADAYA]).map(p => p.name)
        };
    };

    const updateMetricOptions = () => {
        if (activeTab) {
            if ((activeTab === 'last7days' || activeTab === 'last30days') && predictionState.data) {
                setMetricItems(defaultMetricItems.concat(predictionMetricItems));
            } else {
                setMetricItems(defaultMetricItems);
            }
        }
    };

    // Should process/filter/sort the current data, if needed, and then show it.
    const updateViewData = () => {
        //log(`updateViewData`);
    };

    // TODO:
    // Format data for graph on data/metric updates...
    // useEffect(() => {
    //     if (data && tab) {
    //         // If prediction metric is selected, render the graph data with the predictions included
    //         if (selectedMetric === 'prediction') {
    //             let d = renderData(data, tab, selectedMetric, predictionState.data);
    //             return predictionState.data && setFormattedGraphData(d);
    //         }
    //         // Default to rendering the data based on the selected tab and metric
    //         setFormattedGraphData(renderData(data, tab, selectedMetric));
    //     }
    // }, [data, tab, selectedMetric]);

    // Resizing...
    useEffect(() => {
        let resizeId = null;
        let resizeHandler = throttle(() => {
            if (resizeId) window.clearTimeout(resizeId);
            resizeId = window.setTimeout(() => {
                resizeId = null;
                setMobile(isMobile());
            }, 250);
        });

        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    });

    // if (!metricItems.find(mi => mi.value === selectedMetric)) {
    //     setSelectedMetric(metricItems[0].value);
    // }
    // const desktopTest = useMemo(() => {
    //     log(`USE CALLBACK`, activeTab)

    //     return <MerDesktop
    //         activeTab={activeTab}
    //         setActiveTab={setActiveTab}
    //         tabStates={tabStates}
    //         tabLast7daysData={tabStates['last7days']}
    //         tabLast30daysData={tabStates['last30days']}
    //         tabMonthToDateData={tabStates['monthToDate']}
    //         tabQuarterToDateData={tabStates['quarterToDate']}
    //         tabYearToDateData={tabStates['yearToDate']}
    //         //predictionState={predictionState}
    //         merGoal={merGoal}
    //         selectedMetric={selectedMetric}
    //         setSelectedMetric={setSelectedMetric}
    //         metricItems={metricItems}
    //     />
    // }, [tabStates]) //    , [activeTab]);

    const renderDataView = () => {
        return mobile ?
            <MerMobile
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabStates={tabStates}
                tabLast7daysData={tabStates['last7days']}
                tabLast30daysData={tabStates['last30days']}
                tabMonthToDateData={tabStates['monthToDate']}
                tabQuarterToDateData={tabStates['quarterToDate']}
                tabYearToDateData={tabStates['yearToDate']}
                merGoal={merGoal}
                //formattedGraphData={formattedGraphData}
                selectedMetric={selectedMetric}
                setSelectedMetric={setSelectedMetric}
                metricItems={metricItems}
            //predictionState={predictionState}
            />

            :

            <>
                { // todo: refactor state
                    <MerDesktop
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        tabStates={tabStates}
                        tabLast7daysData={tabStates['last7days']}
                        tabLast30daysData={tabStates['last30days']}
                        tabMonthToDateData={tabStates['monthToDate']}
                        tabQuarterToDateData={tabStates['quarterToDate']}
                        tabYearToDateData={tabStates['yearToDate']}
                        //predictionState={predictionState}
                        merGoal={merGoal}
                        selectedMetric={selectedMetric}
                        setSelectedMetric={setSelectedMetric}
                        metricItems={metricItems}
                    />
                }
            </>
    }

    const hasData = () => {
        return true;
    }

    return (
        <div className={'mer-page page-layout ' + (error != undefined ? ' has-error' : '')}>

            <Helmet><title>{Config.SITE_TITLE_PREFIX} - Efficiency Ratio</title></Helmet>

            {(loading || !tabStates) ? renderLoader() :

                // error
                error ? renderError(ErrorMessages.defaultErrorMessage(), error, "large")

                    : !hasData() ? renderNoDataMessage()

                        // loaded but has no data
                        : renderDataView()
            }

        </div>
    )
};

const MerAccounCheckWrapper = ({ auth, ...props }) => {

    const [hasShopifyAccount, setHasShopifyAccount] = useState(null);

    const user = auth?.user;
    const account = user?.activeAccount;

    // If user is not connected to Shopify, don't show MER tab
    useEffect(() => {
        if (auth?.authFinished && account) {
            try {
                let shopifyProvider = getAccountProvider(account, ProviderNames.SHOPIFY);
                setHasShopifyAccount(!!shopifyProvider);
            } catch (e) {
                setHasShopifyAccount(false);
            }
        }
    }, [auth.authFinished]);

    return hasShopifyAccount ?

        <Mer auth={auth} {...props} />

        : hasShopifyAccount === false ?

            <div className="mer-no-account">
                <h2>You must connect your Shopify store to access the MER feature.</h2>
            </div>

            : renderLoader()
};

export default MerAccounCheckWrapper;